/**
 * Helpers for surfacing permission failures from Supabase writes.
 *
 * Two failure modes to handle:
 *
 *   1. RLS-blocked UPDATE / DELETE → Postgres returns success with 0 affected
 *      rows. Supabase's `error` is null. Without an explicit check, the hook
 *      shows a success toast even though nothing happened.
 *
 *   2. RLS-blocked INSERT (or UPDATE with a failing WITH CHECK) → Postgres
 *      throws a real error containing "row-level security policy". Supabase
 *      surfaces this through `error.message`, but the wording is technical.
 *
 * Use `assertRowsAffected` after `.select()`'d writes to convert mode 1 into
 * a real thrown error. Use `toUserMessage` in catch blocks to normalize both
 * modes into a friendly toast.
 */

export class PermissionDeniedError extends Error {
  constructor(actionLabel = "perform this action") {
    super(`You don't have permission to ${actionLabel}.`);
    this.name = "PermissionDeniedError";
  }
}

/**
 * Convert silent "0 rows updated" into an explicit PermissionDeniedError.
 *
 *   const { data, error } = await supabase
 *     .from("pipeline_properties")
 *     .update({ pipeline_id: next })
 *     .eq("id", id)
 *     .select();          // ← must include .select() to get rows back
 *   if (error) throw error;
 *   assertRowsAffected(data, "move this pipeline item");
 */
export function assertRowsAffected(
  data: unknown[] | null | undefined,
  actionLabel = "perform this action",
): void {
  if (!data || data.length === 0) {
    throw new PermissionDeniedError(actionLabel);
  }
}

/**
 * Turn any caught error from a Supabase write into a friendly user-facing
 * message. Detects both our own PermissionDeniedError and the raw Postgres
 * "row-level security policy" error string.
 *
 *   } catch (err) {
 *     toast.error(toUserMessage(err, "Failed to move property"));
 *   }
 */
export function toUserMessage(err: unknown, fallback: string): string {
  if (err instanceof PermissionDeniedError) return err.message;
  const raw = (err as { message?: string })?.message ?? "";
  const code = (err as { code?: string })?.code ?? "";
  const lower = raw.toLowerCase();
  if (
    lower.includes("row-level security") ||
    lower.includes("violates row") ||
    lower.includes("permission denied") ||
    // PGRST116 — .single() / .maybeSingle() got zero rows after a write,
    // almost always because RLS filtered out the target row.
    code === "PGRST116" ||
    lower.includes("json object requested")
  ) {
    return "You don't have permission to do that.";
  }
  return raw || fallback;
}
